/**
 * WorldStatusPanelContainer - Container Component
 *
 * Container component responsible for:
 * - Zustand store connection (useStore, useStorePrimitive)
 * - Computing derived data (active campaign, today's almanac/forecast, moon phases, active events)
 * - Delegating presentation to the pure WorldStatusPanel component
 *
 * This follows the Container/Presenter pattern as defined in ADR-034.
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React, { useMemo } from 'react';
import { useStore, useStorePrimitive } from '../../../../hooks/useStore';
import dayjs from 'dayjs';
import { calculateMoonPhase } from '../../../../utils/world-helpers';
import type { DailyAlmanac, Moon, WeatherForecast, WeatherEvent, Campaign } from '../../../../types/types';
import WorldStatusPanel from './WorldStatusPanel';

export function WorldStatusPanelContainer(): React.ReactElement {
  // Use useStore for gameTime since Date is not a primitive type
  const gameTime = useStore(state => state.session?.general?.gameTime);
  const activeMapId = useStorePrimitive(state => state.session?.map?.activeMapId);
  const activeCampaignId = useStorePrimitive(state => state.session?.general?.activeCampaignId);

  // Use useStore (with shallow equality) for arrays and objects
  const almanac = useStore(state => state.session?.world?.almanac);
  const weatherEvents = useStore(state => state.session?.world?.weatherEvents);
  const moons = useStore(state => state.session?.world?.moons);
  const weatherForecasts = useStore(state => state.session?.world?.weatherForecasts);
  const campaigns = useStore(state => state.session?.world?.campaigns);

  const activeCampaign = useMemo(
    () => (campaigns ?? []).find((c: Campaign) => c.id === activeCampaignId),
    [campaigns, activeCampaignId],
  );

  // Extract the date string to use as a stable dependency instead of the Campaign object
  const activeCampaignGameDate = activeCampaign?.currentGameDate;

  // Use the extracted string value as dependency to avoid object reference instability
  const campaignDate = useMemo(
    () => activeCampaignGameDate ? new Date(activeCampaignGameDate.getTime()) : (gameTime ? new Date(gameTime.getTime()) : null),
    [activeCampaignGameDate, gameTime],
  );

  const todayAlmanac = useMemo(() => {
    const todayString = campaignDate ? dayjs(campaignDate).format('YYYY-MM-DD') : null;
    return (almanac ?? []).find((entry: DailyAlmanac) => dayjs(entry.date).format('YYYY-MM-DD') === todayString);
  }, [almanac, campaignDate]);

  const todayForecast = useMemo(() => {
    if (!activeMapId) return null;
    const forecastsForMap = weatherForecasts[activeMapId] ?? [];
    const todayString = campaignDate ? dayjs(campaignDate).format('YYYY-MM-DD') : null;
    return (forecastsForMap ?? []).find((f: WeatherForecast) => dayjs(f.date).format('YYYY-MM-DD') === todayString);
  }, [weatherForecasts, activeMapId, campaignDate]);

  const activeWeatherEvents = useMemo(() => {
    return (weatherEvents ?? []).filter((event: WeatherEvent) => {
      if (!event.isActive) return false;
      if (!campaignDate) return false;
      const now = campaignDate.getTime();
      const start = event.startDate ? new Date(event.startDate).getTime() : -Infinity;
      const end = event.endDate ? new Date(event.endDate).getTime() : Infinity;
      const isWithinDateRange = now >= start && now <= end;
      if (!isWithinDateRange) return false;
      return !event.mapId || event.mapId === activeMapId;
    });
  }, [weatherEvents, campaignDate, activeMapId]);

  const moonPhases: (Moon & { phase: { name: string; illumination: number } })[] = useMemo(() => {
    if (!campaignDate) return [];
    return (moons ?? []).map((moon: Moon) => ({
      ...moon,
      phase: calculateMoonPhase(campaignDate, moon.cycleDays),
    }));
  }, [moons, campaignDate]);

  return (
    <WorldStatusPanel
      campaignDate={campaignDate}
      todayAlmanac={todayAlmanac}
      todayForecast={todayForecast}
      activeWeatherEvents={activeWeatherEvents}
      moonPhases={moonPhases}
    />
  );
}

export default WorldStatusPanelContainer;
